'use client';
/**
 * Métrologie — la chaîne de raccordement et le registre des étalons (§6.5).
 *
 * ── La question à laquelle cet écran répond ─────────────────────────────────
 *
 * « Par quelle suite d'étalonnages la valeur affichée par cet automate se
 * rattache-t-elle à une référence reconnue ? »
 *
 * C'est le sujet le plus mal compris des techniciens, et l'écran est construit
 * pour ça : la chaîne se lit de gauche à droite, maillon par maillon, et un
 * maillon manquant est VISIBLE — une chaîne interrompue ne se raccorde à rien,
 * et c'est exactement le constat que porte un rapport d'audit.
 *
 * ── Le refus qui structure tout l'affichage ─────────────────────────────────
 *
 * ⚠️ TROIS ÉTATS, JAMAIS ADDITIONNÉS :
 *   · raccordé et documenté — le certificat existe, il est joint, il est valide ;
 *   · raccordé mais sans pièce — quelqu'un l'a affirmé, rien ne l'atteste ;
 *   · non raccordé — personne ne l'a même prétendu.
 *
 * Un compteur unique « raccordés : n » fondrait les deux premiers, et ce serait
 * le chiffre qui rassure un laboratoire jusqu'au jour de la visite. Les trois
 * tuiles ci-dessous sont donc côte à côte, chacune avec son libellé complet, et
 * il n'y a NULLE PART de total.
 *
 * ── Ce que l'écran ne fait pas ──────────────────────────────────────────────
 *
 * Il ne calcule aucun état : tout vient de `GET /ged/metrologie`, qui appelle le
 * module pur `services/raccordement.ts` — le même que celui du dossier de revue
 * de direction. Deux calculs du même raccordement finiraient par se contredire,
 * et c'est le seul sujet où le laboratoire ne peut pas se le permettre.
 *
 * Il n'invente aucune valeur métrologique : EMT, incertitudes et portées se
 * lisent sur les certificats. Quand elles manquent, l'écran le dit.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tableau } from '@/shared/Tableau';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { refusDepot } from '@/lib/depot';
import { useGed } from '@/lib/store';
import {
  ETATS_RACCORDEMENT, ETATS_MAILLON, NATURES_ETALON, RATTACHEMENTS_METRO,
  ETALON_STATUTS, RACCORDEMENTS, EQUIP_CATEGORIES, fmtDate, aujourdhuiIso } from '@/lib/meta';
import { card, inputStyle, EmptyState, FilterBar, RefISO, Skeleton, ConfirmButton, I } from '@/components/ui';
import { useTri, BoutonsTri } from '@/shared/Tri';
import { H1 } from './commun';
import { useAutorisationResponsable, ChampsResponsable } from '@/components/AutorisationResponsable';
import type { GedEtalon, GedChaineEquip, GedEtatMetrologie, GedMaillon, GedVerificationMetro } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
import { peutGererParc } from '@/lib/droits-parc';
const TONES: Record<string, { fond: string; trait: string; texte: string }> = {
  success: { fond: 'var(--green-50)', trait: 'var(--green-600)', texte: 'var(--green-700)' },
  warning: { fond: 'var(--amber-50)', trait: 'var(--amber-500)', texte: 'var(--amber-600)' },
  danger: { fond: 'var(--red-50)', trait: 'var(--red-500)', texte: 'var(--red-600)' },
  muted: { fond: 'var(--gray-25)', trait: 'var(--border-default)', texte: 'var(--text-muted)' },
};
const ton = (t?: string) => TONES[t || 'muted'] || TONES.muted;

const libCategorie = (k: string) => EQUIP_CATEGORIES.find((c) => c.key === k)?.label || k || '—';

/** Contenu d'un fichier en base64, sans le préfixe `data:` du FileReader. */
function enBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('Lecture du fichier impossible.'));
    r.onload = () => resolve(String(r.result || '').split(',')[1] || '');
    r.readAsDataURL(f);
  });
}

/* ───────────────────────────── Les trois états ────────────────────────────── */

/**
 * Une tuile par état. Elles ne se somment pas, et le texte sous les chiffres le
 * dit : c'est la seule protection contre quelqu'un qui, dans six mois, voudra
 * « simplifier » en affichant un pourcentage de conformité.
 */
function TroisEtats({ c }: { c: GedEtatMetrologie['compte'] }) {
  const tuiles = [
    { cle: 'raccorde_documente', n: c.raccordeDocumente },
    { cle: 'raccorde_sans_piece', n: c.raccordeSansPiece },
    { cle: 'non_raccorde', n: c.nonRaccorde },
  ];
  return (
    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
      {tuiles.map((t) => {
        const meta = ETATS_RACCORDEMENT[t.cle];
        const tn = ton(meta.tone);
        return (
          <div key={t.cle} style={{ ...card, padding: '13px 15px', background: tn.fond, borderInlineStart: `3px solid ${tn.trait}` }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: tn.texte, lineHeight: 1.1 }}>{t.n}</div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginTop: 3 }}>{meta.label}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.45 }}>{meta.aide}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────────────────────── La chaîne ──────────────────────────────── */

/** Un maillon. Le libellé de l'état est écrit, jamais seulement coloré : un
 *  daltonien doit lire « maillon manquant », pas deviner un rouge. */
function Maillon({ m, rang }: { m: GedMaillon; rang: number }) {
  const meta = ETATS_MAILLON[m.etat] || ETATS_MAILLON.manquant;
  const tn = ton(meta.tone);
  const casse = m.etat === 'manquant' || m.etat === 'perime';
  return (
    <div style={{
      flex: '1 1 190px', minWidth: 180, padding: '9px 11px',
      background: tn.fond, borderRadius: 'var(--radius-sm)',
      border: `1px solid ${casse ? tn.trait : 'var(--border-subtle)'}`,
      borderInlineStart: `3px solid ${tn.trait}`,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '.04em', textTransform: 'uppercase' }}>
        Maillon {rang} · {m.titre}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: tn.texte, marginTop: 3 }}>{meta.label}</div>
      {m.valeur && <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 3, lineHeight: 1.4 }}>{m.valeur}</div>}
      {m.piece && (
        <a href={`/api/ged/ged/equip/fichiers/${m.piece.cle}/${m.piece.ext}`} target="_blank" rel="noreferrer"
          className="lien-doux" style={{ display: 'inline-block', marginTop: 4, fontSize: 12.5, color: 'var(--navy-700)' }}>
          {m.piece.nom || 'certificat'}
          {m.piece.dateValidite ? ` — valable jusqu’au ${fmtDate(m.piece.dateValidite)}` : ''}
        </a>
      )}
      {/* Ce qui manque, dit à qui doit le corriger — pas un code d'état. C'est
          la moitié de l'intérêt de cet écran : un technicien doit savoir quoi
          aller chercher, pas seulement que « c'est rouge ». */}
      {m.manque && (
        <div style={{ fontSize: 12.5, color: tn.texte, marginTop: 4, lineHeight: 1.45 }}>{m.manque}</div>
      )}
    </div>
  );
}

function LigneChaine({ e }: { e: GedChaineEquip }) {
  const [ouvert, setOuvert] = useState(false);
  const meta = ETATS_RACCORDEMENT[e.etat];
  const tn = ton(meta?.tone);
  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '10px 0' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)', minWidth: 52 }}>{e.code || '—'}</span>
        <span style={{ fontWeight: 700, color: 'var(--text-strong)', minWidth: 170 }}>{e.nom}</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{libCategorie(e.categorie)}</span>
        {e.localisation && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{e.localisation}</span>}
        <span style={{ flex: 1 }} />
        {/* La CHAÎNE ROMPUE est dite séparément de l'état : un instrument peut
            être « raccordé sans pièce » sans que la chaîne soit interrompue
            (un maillon déclaré n'est pas un maillon absent). Fondre les deux
            ferait perdre l'information la plus actionnable. */}
        {e.rompue && (
          <span title="Un maillon au moins est absent ou périmé : la chaîne ne remonte à aucune référence reconnue."
            style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)', cursor: 'help' }}>
            chaîne interrompue
          </span>
        )}
        <span title={meta?.aide}
          style={{ fontSize: 11, fontWeight: 700, color: tn.texte, background: tn.fond, borderRadius: 'var(--radius-pill)', padding: '2px 9px', cursor: 'help' }}>
          {meta?.label || e.etat}
        </span>
        <Bouton taille="sm" type="button" onClick={() => setOuvert((v) => !v)} aria-expanded={ouvert}>
          {ouvert ? 'Replier la chaîne' : 'Voir la chaîne'}
        </Bouton>
      </div>
      {ouvert && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 9, alignItems: 'stretch' }}>
            {e.maillons.map((m, i) => <Maillon key={m.cle} m={m} rang={i + 1} />)}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 7, lineHeight: 1.5 }}>
            {e.derniereOperation ? `Dernière opération le ${fmtDate(e.derniereOperation)}. ` : 'Aucune opération d’étalonnage enregistrée. '}
            {/* Une périodicité non décidée n'est PAS un retard : l'écran le dit
                plutôt que d'afficher une échéance inventée. */}
            {e.verifIntermPeriodiciteMois
              ? `Vérification intermédiaire tous les ${e.verifIntermPeriodiciteMois} mois${e.echeanceVerifInterm ? ` — prochaine le ${fmtDate(e.echeanceVerifInterm)}` : ' — aucune date de départ enregistrée, l’échéance ne peut pas être calculée'}.`
              : 'Aucune périodicité de vérification intermédiaire n’a été fixée pour cet instrument : c’est une décision à prendre, pas un retard.'}
          </div>
        </>
      )}
    </div>
  );
}

/* ──────────────────────── Le registre des étalons ────────────────────────── */

const VIDE: Partial<GedEtalon> = {
  code: '', designation: '', nature: 'etalon_reference', grandeur: '',
  fabricant: '', refFabricant: '', numeroSerie: '', numeroLot: '',
  valeurCertifiee: '', incertitude: '', portee: '', emt: '',
  raccordement: 'aucun', organisme: '', refAccreditation: '',
  rattachementNature: 'inconnu', rattachementLibelle: '',
  refCertificat: '', dateCertificat: '', dateValiditeCertificat: '',
  peremption: '', localisation: '', statut: 'en_service', usage: '', remarque: '',
};

/* Deux colonnes qui se replient d'elles-mêmes sous 200 px : partagé par les
   deux formulaires de l'écran depuis le 26/08/2026. Il vivait dans
   `FormEtalon`, où le formulaire de vérification ne pouvait pas l'atteindre. */
const g2: React.CSSProperties = { display: 'grid', gap: 11, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' };

const label: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 3 };

function Champ({ id, titre, aide, children }: { id: string; titre: string; aide?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} style={label}>{titre}</label>
      {children}
      {aide && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.4 }}>{aide}</div>}
    </div>
  );
}

/**
 * Formulaire d'un étalon.
 *
 * Aucune valeur par défaut « raisonnable » n'est proposée sur les rubriques
 * métrologiques (valeur certifiée, incertitude, étendue, EMT) : elles se lisent
 * sur le certificat du laboratoire. Pré-remplir reviendrait à inventer une
 * donnée d'étalonnage — la faute que ce lot existe pour ne pas commettre.
 */
function FormEtalon({ initial, onFerme, onEnregistre, equipementsMetro }: {
  initial: GedEtalon | null;
  onFerme: () => void;
  onEnregistre: () => void;
  /** Les équipements SOUMIS à métrologie, tels que le moteur les a évalués —
   *  et eux seuls. Proposer un automate ici laisserait croire qu'un étalon peut
   *  le raccorder, alors que la question ne se pose pas pour lui. */
  equipementsMetro: { code: string; nom: string }[];
}) {
  const showToast = useGed((s) => s.showToast);
  const [f, setF] = useState<Partial<GedEtalon>>(() => ({ ...VIDE, ...(initial || {}) }));
  /* ⚠️ CE QUE L'ÉCRAN A LU, FIGÉ À L'OUVERTURE (14/10/2026), joint sous `base`
     en correction : le moteur n'écrit que les rubriques changées depuis, et
     refuse (409) celles qu'un autre a changées entre-temps. */
  const [base] = useState<Partial<GedEtalon>>(() => ({ ...VIDE, ...(initial || {}) }));
  const [enCours, setEnCours] = useState(false);
  const set = (k: keyof GedEtalon, v: string) => setF((p) => ({ ...p, [k]: v }));
  /* `equipements` est le SEUL champ en tableau du formulaire : il a son
     propre poseur plutôt que d'élargir `set` à `string | string[]`, ce qui
     aurait affaibli le type des trente autres champs. */
  const basculerEquip = (code: string) => setF((p) => {
    const actuels = Array.isArray(p.equipements) ? p.equipements : [];
    return { ...p, equipements: actuels.includes(code) ? actuels.filter((x) => x !== code) : [...actuels, code] };
  });

  const enregistrer = async () => {
    setEnCours(true);
    try {
      const rep = await fetchEcriture('/api/ged/ged/metrologie/etalon', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, documentId: initial?.documentId, ...(initial ? { base } : {}) }),
      });
      const d = await rep.json().catch(() => null);
      if (!rep.ok) throw new Error(d?.error?.message || d?.error || 'Enregistrement refusé');
      showToast(initial ? 'Étalon mis à jour — la modification est tracée.' : 'Étalon enregistré. Joignez son certificat pour qu’il documente un raccordement.');
      onEnregistre();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div style={{ ...card, padding: 16, display: 'grid', gap: 14 }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>
        {initial ? `Étalon ${initial.code || initial.ref}` : 'Nouvel étalon ou matériau de référence'}
      </div>

      <div style={g2}>
        <Champ id="et-code" titre="Code interne">
          <input id="et-code" value={f.code || ''} onChange={(e) => set('code', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-des" titre="Désignation *">
          <input id="et-des" value={f.designation || ''} onChange={(e) => set('designation', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-nat" titre="Nature" aide={NATURES_ETALON[f.nature || '']?.aide}>
          <select id="et-nat" value={f.nature || ''} onChange={(e) => set('nature', e.target.value)} style={inputStyle}>
            {Object.entries(NATURES_ETALON).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Champ>
        <Champ id="et-gr" titre="Grandeur mesurée" aide="Température, volume, masse, temps… — ce que cet étalon permet de raccorder.">
          <input id="et-gr" value={f.grandeur || ''} onChange={(e) => set('grandeur', e.target.value)} style={inputStyle} />
        </Champ>
      </div>

      <div style={g2}>
        <Champ id="et-fab" titre="Fabricant">
          <input id="et-fab" value={f.fabricant || ''} onChange={(e) => set('fabricant', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-rf" titre="Référence fabricant">
          <input id="et-rf" value={f.refFabricant || ''} onChange={(e) => set('refFabricant', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-ns" titre="N° de série">
          <input id="et-ns" value={f.numeroSerie || ''} onChange={(e) => set('numeroSerie', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-nl" titre="N° de lot">
          <input id="et-nl" value={f.numeroLot || ''} onChange={(e) => set('numeroLot', e.target.value)} style={inputStyle} />
        </Champ>
      </div>

      <div style={{ background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)', padding: '11px 13px', display: 'grid', gap: 11 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Les quatre rubriques ci-dessous se lisent <strong>sur le certificat</strong>. Laissez-les
          vides tant que personne ne les y a lues : une valeur d’incertitude inventée pour
          « faire propre » vaut moins que rien, elle serait auditée comme une donnée du
          laboratoire.
        </div>
        <div style={g2}>
          <Champ id="et-vc" titre="Valeur certifiée">
            <input id="et-vc" value={f.valeurCertifiee || ''} onChange={(e) => set('valeurCertifiee', e.target.value)} style={inputStyle} />
          </Champ>
          <Champ id="et-inc" titre="Incertitude">
            <input id="et-inc" value={f.incertitude || ''} onChange={(e) => set('incertitude', e.target.value)} style={inputStyle} />
          </Champ>
          <Champ id="et-por" titre="Étendue">
            <input id="et-por" value={f.portee || ''} onChange={(e) => set('portee', e.target.value)} style={inputStyle} />
          </Champ>
          <Champ id="et-emt" titre="Erreur maximale tolérée">
            <input id="et-emt" value={f.emt || ''} onChange={(e) => set('emt', e.target.value)} style={inputStyle} />
          </Champ>
        </div>
      </div>

      <div style={g2}>
        <Champ id="et-rac" titre="Comment CET étalon est-il raccordé ?" aide={RACCORDEMENTS[f.raccordement || '']?.aide}>
          <select id="et-rac" value={f.raccordement || ''} onChange={(e) => set('raccordement', e.target.value)} style={inputStyle}>
            {Object.entries(RACCORDEMENTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Champ>
        <Champ id="et-org" titre="Organisme">
          <input id="et-org" value={f.organisme || ''} onChange={(e) => set('organisme', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-acc" titre="N° d’accréditation de l’organisme" aide="Ce qui distingue un certificat accrédité d’un rapport d’usine.">
          <input id="et-acc" value={f.refAccreditation || ''} onChange={(e) => set('refAccreditation', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-rn" titre="Rattachement final" aide={RATTACHEMENTS_METRO[f.rattachementNature || '']?.aide}>
          <select id="et-rn" value={f.rattachementNature || ''} onChange={(e) => set('rattachementNature', e.target.value)} style={inputStyle}>
            {Object.entries(RATTACHEMENTS_METRO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Champ>
        <Champ id="et-rl" titre="Nommez la référence" aide="« LNE », « ILAC MRA »… — « institut national » sans dire lequel ne se vérifie pas.">
          <input id="et-rl" value={f.rattachementLibelle || ''} onChange={(e) => set('rattachementLibelle', e.target.value)} style={inputStyle} />
        </Champ>
      </div>

      <div style={g2}>
        <Champ id="et-rc" titre="N° de certificat">
          <input id="et-rc" value={f.refCertificat || ''} onChange={(e) => set('refCertificat', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-dc" titre="Date du certificat">
          <input id="et-dc" type="date" value={f.dateCertificat || ''} onChange={(e) => set('dateCertificat', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-dv" titre="Valable jusqu’au" aide="C’est cette date qui dit si le raccordement tient encore, pas celle d’émission.">
          <input id="et-dv" type="date" value={f.dateValiditeCertificat || ''} onChange={(e) => set('dateValiditeCertificat', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-per" titre="Péremption du matériau" aide="Pour un MRC : au-delà, la valeur assignée ne vaut plus.">
          <input id="et-per" type="date" value={f.peremption || ''} onChange={(e) => set('peremption', e.target.value)} style={inputStyle} />
        </Champ>
      </div>

      <div style={g2}>
        <Champ id="et-loc" titre="Localisation">
          <input id="et-loc" value={f.localisation || ''} onChange={(e) => set('localisation', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="et-st" titre="Statut" aide="Un étalon retiré ne raccorde plus rien : les chaînes qui s’appuyaient sur lui s’interrompent.">
          <select id="et-st" value={f.statut || ''} onChange={(e) => set('statut', e.target.value)} style={inputStyle}>
            {Object.entries(ETALON_STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Champ>
      </div>

      {/* ⚠️ CE CHAMP EXISTAIT AU SERVEUR, PAS À L'ÉCRAN (26/08/2026).
          `equipements` était accepté par la route et JOURNALISÉ (« Équipements
          raccordés par cet étalon : … »), mais aucun formulaire ne l'exposait :
          la seule façon de dire quels instruments un étalon raccorde était de
          l'écrire en prose dans « Usage ». C'est le « je n'ai pas la main »
          rapporté par la RQ — la fonction ne manquait pas, sa poignée si.
          En prose, le lien ne se calcule pas : la chaîne de raccordement d'un
          équipement ne peut pas remonter à un étalon nommé dans une phrase. */}
      {/* ⚠️ DEUX CHAMPS POUR UNE SEULE NOTION, ET C'EST VOULU (26/08/2026).
          La phrase du certificat porte des nuances qu'aucun nombre ne rend
          (« composée : incertitude de la balance + masse volumique de l'eau »,
          « incertitude d'assignation, par analyte »). Mais le verdict d'une
          vérification se CALCULE : il lui faut un nombre. Extraire le nombre de
          la phrase donnerait une valeur fausse là où la phrase n'en contient
          pas — et un verdict faussé ne se voit pas. */}
      <div style={g2}>
        <Champ id="et-incv" titre="Incertitude U (k = 2) — valeur"
          aide="Le NOMBRE, repris automatiquement lors des vérifications. Laissé vide, il faudra le saisir à chaque vérification.">
          <input id="et-incv" inputMode="decimal"
            value={f.incertitudeValeur === null || f.incertitudeValeur === undefined ? '' : String(f.incertitudeValeur)}
            onChange={(e) => setF((p) => ({
              ...p,
              /* Vide ⇒ `null`, jamais 0 : zéro dirait « étalon parfait ». */
              incertitudeValeur: e.target.value.trim() === '' ? null : Number(e.target.value.replace(',', '.')),
            }))}
            style={inputStyle} />
        </Champ>
        <Champ id="et-incu" titre="Unité de l’incertitude" aide="Sans elle, la comparaison à une tolérance exprimée autrement n’a pas de sens.">
          <input id="et-incu" value={f.incertitudeUnite || ''} onChange={(e) => set('incertitudeUnite', e.target.value)}
            placeholder="°C, g, µL…" style={inputStyle} />
        </Champ>
      </div>

      <Champ id="et-eq" titre="Équipements raccordés par cet étalon"
        aide="Ce que l’évaluateur demande après le certificat : sur quels instruments cet étalon agit. Seuls les équipements soumis à métrologie sont proposés.">
        {equipementsMetro.length === 0
          ? <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
              Aucun équipement soumis à métrologie n’est déclaré : il n’y a rien à raccorder pour l’instant.
            </p>
          : (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 132, overflowY: 'auto',
              border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: 8,
            }}>
              {equipementsMetro.map((eq) => {
                const choisi = Array.isArray(f.equipements) && f.equipements.includes(eq.code);
                return (
                  <label key={eq.code} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5,
                    padding: '3px 8px', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
                    background: choisi ? 'var(--gray-25)' : 'transparent',
                    border: `1px solid ${choisi ? 'var(--color-primary-active)' : 'var(--border-subtle)'}`,
                  }}>
                    <input type="checkbox" checked={choisi} onChange={() => basculerEquip(eq.code)} />
                    <span><strong>{eq.code}</strong> {eq.nom}</span>
                  </label>
                );
              })}
            </div>
          )}
      </Champ>

      <Champ id="et-us" titre="Usage qui en est fait" aide="À quoi sert cet étalon, et sur quels instruments — c’est ce que l’évaluateur demande après le certificat.">
        <textarea id="et-us" value={f.usage || ''} onChange={(e) => set('usage', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      </Champ>
      <Champ id="et-rq" titre="Remarque">
        <textarea id="et-rq" value={f.remarque || ''} onChange={(e) => set('remarque', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      </Champ>

      <PiedActions taille="sm" annuler={{ onClick: onFerme }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: enCours, disabled: enCours || !String(f.designation || '').trim(), onClick: enregistrer }} />
    </div>
  );
}

/** Joindre le certificat — le geste qui fait passer de « déclaré » à
 *  « documenté ». La date de fin de validité est demandée, pas déduite d'une
 *  périodicité : une périodicité est une intention, une date de validité est un
 *  engagement écrit sur le document. */
function JoindreCertificat({ etalon, onFait }: { etalon: GedEtalon; onFait: () => void }) {
  const showToast = useGed((s) => s.showToast);
  const ref = useRef<HTMLInputElement>(null);
  const [validite, setValidite] = useState(etalon.dateValiditeCertificat || '');
  const [enCours, setEnCours] = useState(false);

  const envoyer = async () => {
    const fichier = ref.current?.files?.[0];
    if (!fichier) return showToast('Choisissez le fichier du certificat (PDF, JPG ou PNG).', 'erreur');
    // Poids ET contenu réel, nommés dans le refus (SEC-10 b) : « trop
    // volumineux » seul n'apprenait pas de combien on dépasse.
    const refus = await refusDepot('technique', fichier, 'Le certificat');
    if (refus) return showToast(refus, 'erreur');
    setEnCours(true);
    try {
      const base64 = await enBase64(fichier);
      const rep = await fetchEcriture(`/api/ged/ged/metrologie/etalon/${etalon.documentId}/certificat`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: fichier.name, base64, dateValidite: validite }),
      });
      const d = await rep.json().catch(() => null);
      if (!rep.ok) throw new Error(d?.error?.message || d?.error || 'Envoi refusé');
      showToast(validite
        ? 'Certificat joint — cet étalon documente désormais un raccordement.'
        : 'Certificat joint, mais SANS date de fin de validité : le raccordement reste « déclaré », pas « documenté ».');
      if (ref.current) ref.current.value = '';
      onFait();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Envoi refusé', 'erreur');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 9 }}>
      <div>
        <label htmlFor={`cert-f-${etalon.documentId}`} style={label}>Certificat (PDF, JPG, PNG)</label>
        <input id={`cert-f-${etalon.documentId}`} ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ fontSize: 12.5 }} />
      </div>
      <div>
        <label htmlFor={`cert-v-${etalon.documentId}`} style={label}>Valable jusqu’au</label>
        <input id={`cert-v-${etalon.documentId}`} type="date" value={validite} onChange={(e) => setValidite(e.target.value)}
          style={{ ...inputStyle, width: 160 }} />
      </div>
      <Bouton variante="primaire" taille="sm" type="button" onClick={envoyer} disabled={enCours}>
        {enCours ? 'Envoi…' : 'Joindre le certificat'}
      </Bouton>
    </div>
  );
}

function LigneEtalon({ e, peutParc, onEditer, onChange }: {
  e: GedEtalon; peutParc: boolean; onEditer: () => void; onChange: () => void;
}) {
  const showToast = useGed((s) => s.showToast);
  const [ouvert, setOuvert] = useState(false);
  const meta = ETATS_RACCORDEMENT[e.etat || 'non_raccorde'];
  const tn = ton(meta?.tone);
  const pieces = e.pieces || [];

  const retirer = async (pieceId: string) => {
    try {
      const rep = await fetchEcriture(`/api/ged/ged/metrologie/etalon/${e.documentId}/piece/${pieceId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!rep.ok) throw new Error('Retrait refusé');
      showToast('Pièce retirée — le retrait est tracé au journal.');
      onChange();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Retrait refusé', 'erreur');
    }
  };

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '10px 0' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)', minWidth: 74 }}>{e.code || e.ref}</span>
        {/* Le journal suit la référence, comme partout (règle RQ du 18/08,
            câblée le 24/08/2026) : les certificats joints et retraits
            s'écrivaient sans que rien ne les montre ici. */}
        <BoutonJournal reference={e.ref} quoi="de cet étalon" />
        <span style={{ fontWeight: 700, color: 'var(--text-strong)', minWidth: 180 }}>{e.designation}</span>
        <span title={NATURES_ETALON[e.nature]?.aide} style={{ fontSize: 12.5, color: 'var(--text-muted)', cursor: 'help' }}>
          {NATURES_ETALON[e.nature]?.label || e.nature}
        </span>
        {e.grandeur && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{e.grandeur}</span>}
        {e.statut === 'retire' && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>retiré du service</span>}
        <span style={{ flex: 1 }} />
        {e.validiteRetenue && (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>valable jusqu’au {fmtDate(e.validiteRetenue)}</span>
        )}
        <span title={meta?.aide}
          style={{ fontSize: 11, fontWeight: 700, color: tn.texte, background: tn.fond, borderRadius: 'var(--radius-pill)', padding: '2px 9px', cursor: 'help' }}>
          {meta?.label || e.etat}
        </span>
        <Bouton taille="sm" type="button" onClick={() => setOuvert((v) => !v)} aria-expanded={ouvert}>
          {ouvert ? 'Replier' : 'Détail'}
        </Bouton>
      </div>

      {/* La PHRASE qui dit pourquoi l'état est celui-là. Un état sans son motif
          se lit comme un verdict arbitraire, et personne ne sait quoi corriger. */}
      {e.explication && (
        <div style={{ fontSize: 12.5, color: tn.texte, marginTop: 4, lineHeight: 1.45 }}>{e.explication}</div>
      )}

      {ouvert && (
        <div style={{ marginTop: 9, display: 'grid', gap: 8, fontSize: 12.5 }}>
          <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
            {([
              ['Fabricant', [e.fabricant, e.refFabricant].filter(Boolean).join(' · ')],
              ['N° de série / lot', [e.numeroSerie, e.numeroLot].filter(Boolean).join(' · ')],
              ['Valeur certifiée', e.valeurCertifiee],
              ['Incertitude', e.incertitude],
              ['Étendue', e.portee],
              ['Erreur maximale tolérée', e.emt],
              ['Organisme', [e.organisme, e.refAccreditation && `accréditation ${e.refAccreditation}`].filter(Boolean).join(' — ')],
              ['Rattachement final', [RATTACHEMENTS_METRO[e.rattachementNature]?.label, e.rattachementLibelle].filter(Boolean).join(' — ')],
              ['Certificat', [e.refCertificat, e.dateCertificat && `du ${fmtDate(e.dateCertificat)}`].filter(Boolean).join(' ')],
              ['Localisation', e.localisation],
            ] as [string, string][]).map(([t, v]) => (
              <div key={t}>
                <div style={label}>{t}</div>
                {/* « Non renseigné » EN TOUTES LETTRES plutôt qu'un tiret :
                    l'absence d'une incertitude n'est pas un détail de mise en
                    page, c'est une information manquante du dossier. */}
                <div style={{ fontSize: 12.5, color: v ? 'var(--text-body)' : 'var(--text-muted)', fontStyle: v ? 'normal' : 'italic' }}>
                  {v || 'non renseigné'}
                </div>
              </div>
            ))}
          </div>
          {e.usage && <div><div style={label}>Usage</div><div style={{ fontSize: 12.5 }}>{e.usage}</div></div>}
          {e.remarque && <div><div style={label}>Remarque</div><div style={{ fontSize: 12.5 }}>{e.remarque}</div></div>}

          <div>
            <div style={label}>Documents au dossier</div>
            {pieces.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Aucun document. Tant qu’aucun certificat n’est joint, ce raccordement est déclaré, pas documenté.
              </div>
            )}
            {pieces.map((p) => (
              <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5, padding: '3px 0' }}>
                <a href={`/api/ged/ged/equip/fichiers/${p.cle}/${p.ext}`} target="_blank" rel="noreferrer"
                  className="lien-doux" style={{ color: 'var(--navy-700)' }}>{p.name}</a>
                {p.id === e.certificatPieceId && (
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--green-700)' }}>certificat en vigueur</span>
                )}
                <span style={{ color: 'var(--text-muted)' }}>
                  {p.dateValidite ? `valable jusqu’au ${fmtDate(p.dateValidite)}` : 'sans date de fin de validité'}
                </span>
                {peutParc && (
                  <ConfirmButton onConfirm={() => retirer(p.id)} label="Supprimer" variant="danger" confirmer="Supprimer"
                    question={p.id === e.certificatPieceId
                      ? 'Supprimer le certificat EN VIGUEUR ? Le fichier est effacé et cet étalon repasse « raccordement déclaré, non documenté » — avec lui, toutes les chaînes qui s’y appuient.'
                      : 'Supprimer ce document ? Le fichier est effacé du disque ; la suppression reste au journal.'} />
                )}
              </div>
            ))}
          </div>

          {peutParc && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Bouton taille="sm" type="button" onClick={onEditer}>Modifier la fiche</Bouton>
              {/* Lot 3 (11/08/2026) — le retrait du service avait un état affiché
                  mais aucun bouton : il ne se faisait qu'en réécrivant la fiche. */}
              <ConfirmButton
                label={e.statut === 'retire' ? 'Remettre en service' : 'Retirer du service'}
                variant="outline"
                question={e.statut === 'retire'
                  ? 'Remettre cet étalon en service ? Il redevient utilisable pour raccorder des instruments.'
                  : 'Retirer cet étalon du service ? Rien n’est effacé : la fiche, ses certificats et son historique restent — mais il ne doit plus servir à raccorder.'}
                onConfirm={async () => {
                  const rep = await fetchEcriture('/api/ged/ged/metrologie/etalon', {
                    method: 'POST', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ documentId: e.documentId, statut: e.statut === 'retire' ? 'en_service' : 'retire' }),
                  });
                  if (!rep.ok) { showToast('Changement de statut refusé.', 'erreur'); return; }
                  showToast(e.statut === 'retire' ? 'Étalon remis en service.' : 'Étalon retiré du service — tracé au journal.');
                  onChange();
                }} />
            </div>
          )}
          {peutParc && <JoindreCertificat etalon={e} onFait={onChange} />}
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Le registre des vérifications (26/08/2026) ────────────── */

const VERDICTS: Record<string, { label: string; fond: string; texte: string }> = {
  conforme: { label: 'Conforme', fond: 'var(--gray-25)', texte: 'var(--text-body)' },
  non_conforme: { label: 'NON conforme', fond: 'var(--red-50)', texte: 'var(--red-600)' },
  /* ⚠️ AMBRE, PAS ROUGE. « Indécidable » ne dit pas que l'instrument est mauvais :
     il dit que personne n'a fixé sa tolérance. Le peindre comme une
     non-conformité ferait chercher une panne là où il manque une décision. */
  indecidable: { label: 'Indécidable', fond: 'var(--amber-50)', texte: 'var(--amber-600)' },
};

interface ToleranceParc {
  code: string; nom: string;
  emt: number | null; emtRelativePct: number | null;
  unite: string; phraseEmt: string; periodiciteMois: number | null;
}

function PanneauVerifications({ equipements, etalons, estRQ, peutParc }: {
  equipements: GedChaineEquip[]; etalons: GedEtalon[]; estRQ: boolean; peutParc: boolean;
}) {
  const showToast = useGed((s) => s.showToast);
  const [lignes, setLignes] = useState<GedVerificationMetro[] | null>(null);
  /* L6 (10/10/2026) : un échec de lecture se DIT (erreur + Réessayer), il ne se
     déguise plus en liste vide — « aucune vérification » est une affirmation. */
  const [erreurLecture, setErreurLecture] = useState('');
  const [compte, setCompte] = useState<{ total: number; conformes: number; nonConformes: number; indecidables: number; annulees: number } | null>(null);
  const [saisie, setSaisie] = useState(false);
  const [tolerances, setTolerances] = useState<ToleranceParc[]>([]);
  const [aAnnuler, setAAnnuler] = useState<GedVerificationMetro | null>(null);
  const [motifAnn, setMotifAnn] = useState('');
  // Le RQ annule depuis sa propre session ; sinon un responsable s'authentifie
  // pour ce seul geste, depuis le poste du technicien (06/09/2026).
  const authResponsable = useAutorisationResponsable();

  const charger = useCallback(() => {
    void fetch('/api/ged/ged/metrologie/verifications', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setErreurLecture(''); setLignes((d?.lignes || []) as GedVerificationMetro[]); setCompte(d?.compte || null); setTolerances((d?.tolerances || []) as ToleranceParc[]); })
      .catch(() => { setErreurLecture('Les vérifications n’ont pas pu être lues : le serveur n’a pas répondu.'); setCompte(null); });
  }, []);
  useEffect(() => { charger(); }, [charger]);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55 }}>
        Ce que la vérification a <strong>trouvé</strong>, et pas seulement sa date : la valeur de
        l’étalon, celle qu’a affichée l’instrument, l’écart, et le verdict qui en découle.
        Le verdict est <strong>calculé</strong>, jamais saisi — il se recalcule à l’identique devant
        un évaluateur. Une vérification ne se modifie pas : une erreur se corrige par une
        nouvelle vérification qui cite la précédente.
      </p>

      {compte && compte.total > 0 && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12.5 }}>
          <span><strong>{compte.total}</strong> vérification(s)</span>
          <span style={{ color: 'var(--text-muted)' }}>·</span>
          <span>{compte.conformes} conforme(s)</span>
          {compte.nonConformes > 0 && (
            <span style={{ color: 'var(--red-600)', fontWeight: 700 }}>{compte.nonConformes} NON conforme(s)</span>
          )}
          {/* Compté à part, jamais additionné aux non-conformes. */}
          {compte.indecidables > 0 && (
            <span style={{ color: 'var(--amber-600)' }}>{compte.indecidables} indécidable(s) — tolérance non fixée</span>
          )}
          {/* Dites, mais hors du total : elles ne comptent plus. */}
          {compte.annulees > 0 && (
            <span style={{ color: 'var(--text-muted)' }}>{compte.annulees} annulée(s), hors comptes</span>
          )}
        </div>
      )}

      {peutParc && !saisie && (
        <div>
          <Bouton variante="primaire" taille="sm" type="button" onClick={() => setSaisie(true)}>
            Consigner une vérification
          </Bouton>
        </div>
      )}
      {saisie && (
        <FormVerification equipements={equipements} etalons={etalons} tolerances={tolerances}
          onFerme={() => setSaisie(false)}
          onEnregistre={(alerte) => {
            setSaisie(false); charger();
            /* La variante par défaut EST le succès sur ce dépôt : « succes »
               n'existe pas. Une non-conformité, elle, part en « erreur » — le
               ton doit correspondre à ce qui vient d'être constaté. */
            showToast(alerte || 'Vérification consignée.', alerte ? 'erreur' : undefined);
          }} />
      )}

      {aAnnuler && (
        <div style={{ ...card, padding: 14, display: 'grid', gap: 8, borderInlineStart: '3px solid var(--amber-500)' }}>
          <strong style={{ fontSize: 13 }}>Annuler la vérification {aAnnuler.ref} ?</strong>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
            Rien n’est effacé : le constat reste au registre, barré, avec ce motif et votre nom — et
            il sort des comptes. <strong>Une annulation ne se défait pas</strong> : pour rétablir un
            constat, enregistrez une nouvelle vérification.
          </p>
          <input value={motifAnn} onChange={(e) => setMotifAnn(e.target.value)} style={inputStyle}
            placeholder="Pourquoi ce constat ne compte pas (10 caractères au minimum)" aria-label="Motif de l’annulation" />
          {/* Réservée au RQ — ou à un responsable qui s'authentifie ici même
              pour ce seul geste, sans ouvrir de session (06/09/2026). */}
          {!estRQ && <ChampsResponsable {...authResponsable} libelle="cette annulation" />}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Bouton taille="sm" type="button" onClick={() => { setAAnnuler(null); setMotifAnn(''); authResponsable.reinitialiser(); }}>Renoncer</Bouton>
            <Bouton variante="primaire" taille="sm" type="button" disabled={motifAnn.trim().length < 10 || (!estRQ && !authResponsable.pret)} onClick={async () => {
                const rep = await fetchEcriture(`/api/ged/ged/metrologie/verification/${aAnnuler.documentId}/annuler`, {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ motif: motifAnn.trim(), responsable: authResponsable.payload }),
                });
                const d = await rep.json().catch(() => null);
                if (!rep.ok) { showToast(d?.error?.message || 'Annulation refusée.', 'erreur'); return; }
                setAAnnuler(null); setMotifAnn(''); authResponsable.reinitialiser(); charger();
                showToast(String(d?.message || 'Vérification annulée.'));
              }}>Annuler la vérification</Bouton>
          </div>
        </div>
      )}

      {/* L6 (10/10/2026) : le tableau du socle, niveau STANDARD — « Lu », « Écart »,
          « Tolérance », « Référence » en nombres alignés à droite (É-4), verdict et
          grandeur en liste, « Annuler » en colonne d'actions, états intégrés. */}
      <Tableau
        id="metrologie-verifications"
        ajusterLargeur
        niveau="standard"
        defilementHorizontal
        chargement={lignes === null && !erreurLecture}
        erreur={erreurLecture || null}
        onReessayer={charger}
        vide={{ titre: 'Aucune vérification consignée', indice: 'Les échéances de vérification intermédiaire sont suivies sur la chaîne du parc, mais aucun résultat n’a encore été enregistré ici.' }}
        lignes={lignes || []}
        cleLigne={(v) => v.documentId || v.ref}
        ligneProps={(v) => ({ title: v.phrase, style: { opacity: v.annule ? 0.5 : 1, textDecoration: v.annule ? 'line-through' : 'none' } })}
        colonnes={[
          { cle: 'date', libelle: 'Date', type: 'date', valeur: (v) => v.date, rendu: (v) => fmtDate(v.date) },
          { cle: 'instrument', libelle: 'Instrument', type: 'liste', epinglee: true, obligatoire: true, valeur: (v) => v.equipementCode, rendu: (v) => <strong>{v.equipementCode}</strong> },
          /* Sans étalon, la vérification ne raccorde rien : on l'écrit plutôt que de
             laisser une case vide qu'on lirait comme un oubli. */
          { cle: 'etalon', libelle: 'Étalon', type: 'liste', valeur: (v) => v.etalonRef || '', rendu: (v) => v.etalonRef || <span style={{ color: 'var(--amber-600)' }}>sans étalon</span> },
          { cle: 'grandeur', libelle: 'Grandeur', type: 'liste', valeur: (v) => v.grandeur, rendu: (v) => v.grandeur },
          { cle: 'reference', libelle: 'Référence', type: 'nombre', valeur: (v) => v.valeurReference ?? null, rendu: (v) => <>{v.valeurReference ?? '—'} {v.unite}</> },
          { cle: 'lu', libelle: 'Lu', type: 'nombre', valeur: (v) => v.valeurLue ?? null, rendu: (v) => <>{v.valeurLue ?? '—'} {v.unite}</> },
          {
            /* Le signe est CONSERVÉ à l'affichage : trop haut et trop bas n'appellent pas le même réglage. */
            cle: 'ecart', libelle: 'Écart', type: 'nombre', valeur: (v) => v.ecart, style: { fontWeight: 700 },
            rendu: (v) => (
              <>
                {v.ecart === null ? '—' : `${v.ecart > 0 ? '+' : ''}${v.ecart}`}
                {v.ecartMajore !== null && v.ecart !== null && Math.abs(v.ecart) !== v.ecartMajore && (
                  <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> (→ {v.ecartMajore})</span>
                )}
              </>
            ),
          },
          {
            /* La tolérance RETENUE, pas celle saisie : quand une absolue et une relative
               coexistent, c'est la plus permissive qui a décidé, et c'est elle qu'un
               évaluateur doit lire. */
            cle: 'tolerance', libelle: 'Tolérance', type: 'nombre',
            valeur: (v) => (v.toleranceRetenue !== null && v.toleranceRetenue !== undefined ? v.toleranceRetenue : v.emt),
            rendu: (v) => (v.toleranceRetenue !== null && v.toleranceRetenue !== undefined
              ? <>±{v.toleranceRetenue}{v.emtRelativePct ? <span style={{ color: 'var(--text-muted)' }}> ({v.emtRelativePct} %)</span> : null}</>
              : v.emt === null ? '—' : `±${v.emt}`),
          },
          {
            cle: 'verdict', libelle: 'Verdict', type: 'liste', valeur: (v) => v.verdict,
            libelleValeur: (k) => (VERDICTS[k] || VERDICTS.indecidable).label, style: { whiteSpace: 'normal' },
            rendu: (v) => {
              const vd = VERDICTS[v.verdict] || VERDICTS.indecidable;
              return (
                <>
                  <span style={{ background: vd.fond, color: vd.texte, padding: '2px 8px', borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700 }}>
                    {vd.label}
                  </span>
                  {/* ⚠️ L'ANNULATION SE VOIT, elle ne fait pas disparaître : le constat reste
                      lisible, barré, avec son motif. */}
                  {v.annule && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                      annulée — {v.motifAnnulation}
                    </div>
                  )}
                </>
              );
            },
          },
        ]}
        /* Visible de tous (06/09/2026) : c'est le mot de passe du responsable qui protège le geste. */
        actions={(v) => (!v.annule ? <Bouton taille="sm" type="button" onClick={() => setAAnnuler(v)}>Annuler</Bouton> : null)}
      />
    </div>
  );
}

function FormVerification({ equipements, etalons, tolerances, onFerme, onEnregistre }: {
  equipements: GedChaineEquip[]; etalons: GedEtalon[]; tolerances: ToleranceParc[];
  onFerme: () => void; onEnregistre: (alerte: string) => void;
}) {
  const [f, setF] = useState({
    date: aujourdhuiIso(),
    equipementCode: '', etalonRef: '', nature: 'intermediaire',
    grandeur: '', unite: '', valeurReference: '', valeurLue: '',
    emt: '', emtRelativePct: '', incertitudeEtalon: '', periodiciteMois: '', note: '',
  });
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));

  /* CHOISIR L'INSTRUMENT PRÉ-REMPLIT SA TOLÉRANCE (26/08/2026).
     ⚠️ On ne remplace QUE les champs restés vides : quelqu'un qui a déjà saisi
     une tolérance au point mesuré ne doit pas se la voir écraser en changeant
     d'instrument par erreur. Et si l'instrument n'en porte aucune, on ne vide
     rien — on laisse le verdict devenir « indécidable », qui est la vérité. */
  const choisirInstrument = (code: string) => {
    const t = tolerances.find((x) => x.code === code);
    setF((p) => ({
      ...p,
      equipementCode: code,
      emt: p.emt || (t?.emt !== null && t?.emt !== undefined ? String(t.emt) : ''),
      emtRelativePct: p.emtRelativePct || (t?.emtRelativePct !== null && t?.emtRelativePct !== undefined ? String(t.emtRelativePct) : ''),
      unite: p.unite || t?.unite || '',
      periodiciteMois: p.periodiciteMois || (t?.periodiciteMois ? String(t.periodiciteMois) : ''),
    }));
  };
  const toleranceDe = tolerances.find((x) => x.code === f.equipementCode);
  /* Un étalon RETIRÉ ne raccorde plus rien : le serveur le refuse, l'écran ne
     le propose donc pas — un choix offert puis refusé est une impasse. */
  const etalonsUtilisables = etalons.filter((e) => e.statut !== 'retire');

  const enregistrer = async () => {
    setEnCours(true); setErreur('');
    try {
      const rep = await fetchEcriture('/api/ged/ged/metrologie/verification', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(f),
      });
      const d = await rep.json().catch(() => null);
      if (!rep.ok) throw new Error(d?.error?.message || 'Enregistrement refusé');
      onEnregistre(String(d?.alerte || ''));
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement refusé');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
      <div style={g2}>
        <Champ id="v-date" titre="Date de la vérification">
          <input id="v-date" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="v-eq" titre="Instrument vérifié" aide="Seuls les équipements soumis à métrologie sont proposés.">
          <select id="v-eq" value={f.equipementCode} onChange={(e) => choisirInstrument(e.target.value)} style={inputStyle}>
            <option value="">choisir…</option>
            {equipements.map((e) => <option key={e.code} value={e.code}>{e.code} — {e.nom}</option>)}
          </select>
        </Champ>
      </div>
      <div style={g2}>
        <Champ id="v-eta" titre="Étalon utilisé"
          aide="Sans étalon, la vérification ne raccorde rien — c’est un troisième état, jamais « vérifié ».">
          <select id="v-eta" value={f.etalonRef} onChange={(e) => set('etalonRef', e.target.value)} style={inputStyle}>
            <option value="">aucun étalon nommé</option>
            {etalonsUtilisables.map((e) => <option key={e.ref} value={e.ref}>{e.ref} — {e.designation}</option>)}
          </select>
        </Champ>
        <Champ id="v-nat" titre="Nature">
          <select id="v-nat" value={f.nature} onChange={(e) => set('nature', e.target.value)} style={inputStyle}>
            <option value="intermediaire">Vérification intermédiaire</option>
            <option value="apres_reparation">Après réparation ou intervention</option>
            <option value="reception">À la réception de l’instrument</option>
          </select>
        </Champ>
      </div>
      <div style={g2}>
        <Champ id="v-gr" titre="Grandeur mesurée" aide="Température, masse, volume…">
          <input id="v-gr" value={f.grandeur} onChange={(e) => set('grandeur', e.target.value)} style={inputStyle} />
        </Champ>
        <Champ id="v-un" titre="Unité">
          <input id="v-un" value={f.unite} onChange={(e) => set('unite', e.target.value)} placeholder="°C, g, µL…" style={inputStyle} />
        </Champ>
      </div>
      <div style={g2}>
        <Champ id="v-ref" titre="Valeur de l’étalon" aide="Ce que l’étalon garantit.">
          <input id="v-ref" value={f.valeurReference} onChange={(e) => set('valeurReference', e.target.value)} inputMode="decimal" style={inputStyle} />
        </Champ>
        <Champ id="v-lu" titre="Valeur lue sur l’instrument" aide="Ce que l’instrument a affiché.">
          <input id="v-lu" value={f.valeurLue} onChange={(e) => set('valeurLue', e.target.value)} inputMode="decimal" style={inputStyle} />
        </Champ>
      </div>
      <div style={g2}>
        <Champ id="v-emt" titre="Erreur maximale tolérée (±)"
          aide="⚠️ Sans elle, le verdict sera « indécidable » — jamais « conforme ». Un instrument dont personne n’a fixé la tolérance ne peut pas être déclaré bon.">
          <input id="v-emt" value={f.emt} onChange={(e) => set('emt', e.target.value)} inputMode="decimal" style={inputStyle} />
        </Champ>
        <Champ id="v-emtr" titre="… ou tolérance en % de la référence"
          aide="⚠️ Pour les tolérances de constructeur exprimées en pourcentage — « ± 1 % de la vitesse affichée », ISO 8655. Saisir « 1 » dans la case ci-contre donnerait un verdict faux : sur 3 000 tr/min, ± 1 % vaut 30, pas 1. Si les deux sont données, la plus permissive décide.">
          <input id="v-emtr" value={f.emtRelativePct} onChange={(e) => set('emtRelativePct', e.target.value)} inputMode="decimal" style={inputStyle} />
        </Champ>
      </div>
      <div style={g2}>
        <Champ id="v-inc" titre="Incertitude de l’étalon (±)"
          aide="Laissée vide, celle du certificat de l’étalon est reprise. Elle entre dans la décision : un même écart peut être conforme avec un étalon fin et non conforme avec un étalon large.">
          <input id="v-inc" value={f.incertitudeEtalon} onChange={(e) => set('incertitudeEtalon', e.target.value)} inputMode="decimal" style={inputStyle} />
        </Champ>
      </div>
      <div style={g2}>
        <Champ id="v-per" titre="Périodicité (mois)" aide="Sert à calculer la prochaine échéance. Vide : aucune échéance n’est inventée.">
          <input id="v-per" value={f.periodiciteMois} onChange={(e) => set('periodiciteMois', e.target.value)} inputMode="numeric" style={inputStyle} />
        </Champ>
        <Champ id="v-note" titre="Remarque">
          <input id="v-note" value={f.note} onChange={(e) => set('note', e.target.value)} style={inputStyle} />
        </Champ>
      </div>
      {/* Ce que la fiche de l'instrument DIT de sa tolérance, en toutes lettres :
          le nombre pré-rempli vient de là, et la phrase porte les nuances
          (norme, condition) qu'il ne rend pas. */}
      {toleranceDe?.phraseEmt && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          <I n="info" s={12} /> Fiche de {toleranceDe.nom} : « {toleranceDe.phraseEmt} »
          {toleranceDe.emt === null && toleranceDe.emtRelativePct === null && (
            <strong style={{ color: 'var(--amber-600)' }}> — aucune tolérance chiffrée n’est enregistrée sur cet instrument : le verdict sera « indécidable » tant qu’elle n’est pas saisie.</strong>
          )}
        </p>
      )}
      {erreur && <p role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5, margin: 0 }}>{erreur}</p>}
      <PiedActions taille="sm" annuler={{ onClick: onFerme }} principale={{ libelle: 'Consigner', libelleChargement: 'Enregistrement…', chargement: enCours, disabled: enCours || !f.equipementCode, onClick: enregistrer }} />
    </div>
  );
}

/* ────────────────────────────────── L'écran ───────────────────────────────── */

/* Les clés de tri de la chaîne du parc — hors composant : `useTri` les garde en
   dépendance, une référence neuve à chaque rendu relancerait le tri pour rien. */
const TRI_CHAINE_DEFAUT = { cle: 'code', sens: 1 as const };
const VALEURS_CHAINE: Record<string, (e: GedChaineEquip) => string> = {
  code: (e) => e.code || '', nom: (e) => e.nom, lieu: (e) => e.localisation || '', etat: (e) => e.etat,
};

export function Metrologie() {
  const session = useGed((s) => s.session);
  const estRQ = session.roles.includes('rq'); // l'annulation d'une vérification reste au RQ (autoriserResponsable)
  const peutParc = peutGererParc(session.roles);
  const [etat, setEtat] = useState<GedEtatMetrologie | null>(null);
  const [chargement, setChargement] = useState(true);
  const [onglet, setOnglet] = useState<'chaine' | 'etalons' | 'verifs'>('chaine');
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  // Tri de la chaîne du parc — par défaut le code d'inventaire, qui est l'ordre
  // du classeur ; le nom se trie tout aussi bien.
  /* L4 (10/10/2026) : le MÊME moteur de tri que les tableaux (`useTri`), plus
     de `SortableTh`. Par défaut le code d'inventaire, l'ordre du classeur. */
  const { tri, basculer: onTri, trier } = useTri<GedChaineEquip>(TRI_CHAINE_DEFAUT, VALEURS_CHAINE);
  const [edition, setEdition] = useState<{ ouvert: boolean; etalon: GedEtalon | null }>({ ouvert: false, etalon: null });

  /** Ne pose PAS `chargement` à vrai : l'état part déjà à vrai, et le faire ici
   *  serait un `setState` synchrone dans l'effet — une erreur de lint sur ce
   *  dépôt, et une cascade de rendus. Un rechargement après écriture n'a de
   *  toute façon pas besoin de vider l'écran : la liste se met à jour en place. */
  const charger = useCallback(() => {
    return fetch('/api/ged/ged/metrologie', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: GedEtatMetrologie | null) => setEtat(d))
      .catch(() => setEtat(null))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  const c = etat?.compte;
  // Mémorisés : un `|| []` littéral rendrait une nouvelle référence à chaque
  // rendu et relancerait les filtres ci-dessous sans qu'aucune donnée n'ait bougé.
  const equipements = useMemo(() => etat?.equipements || [], [etat]);
  const etalons = useMemo(() => etat?.etalons || [], [etat]);

  const chaines = useMemo(() => {
    const base = filtre === 'documente' ? equipements.filter((e) => e.etat === 'raccorde_documente')
      : filtre === 'sanspiece' ? equipements.filter((e) => e.etat === 'raccorde_sans_piece')
        : filtre === 'nonraccorde' ? equipements.filter((e) => e.etat === 'non_raccorde')
          : filtre === 'rompues' ? equipements.filter((e) => e.rompue)
            : equipements;
    const qq = q.trim().toLowerCase();
    const cherche = qq ? base.filter((e) => `${e.code} ${e.nom} ${e.localisation} ${e.organisme}`.toLowerCase().includes(qq)) : base;
    // Code et désignation sont déjà DEUX colonnes alignées sur la ligne ; il
    // leur manquait de pouvoir se trier — on cherche un instrument tantôt par
    // « EQ03 », tantôt par « Erba Lyte Pro ».
    return trier(cherche);
  }, [equipements, filtre, q, trier]);

  const listeEtalons = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return qq ? etalons.filter((e) => `${e.code} ${e.ref} ${e.designation} ${e.grandeur} ${e.organisme}`.toLowerCase().includes(qq)) : etalons;
  }, [etalons, q]);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <H1 eyebrow={<>Ressources · <RefISO code="6.5" /></>} title="Métrologie — chaîne de raccordement" />
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Par quelle suite d’étalonnages la valeur affichée par un instrument se rattache-t-elle
        à une référence reconnue ? La chaîne se lit en quatre maillons — l’instrument, l’étalon
        de référence, l’organisme et son accréditation, puis le rattachement national ou
        international. <strong>Un maillon manquant interrompt la chaîne</strong> : elle ne
        raccorde alors à rien, quel que soit l’état des maillons suivants.
      </p>

      {chargement && <Skeleton rows={4} />}

      {!chargement && !etat && (
        <EmptyState titre="L’état du raccordement n’a pas pu être lu."
          indice="Réessayez ; si le problème persiste, il s’agit d’un défaut d’autorisation côté serveur, pas d’une absence de données." />
      )}

      {!chargement && etat && c && (
        <>
          <TroisEtats c={c} />
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
            Ces trois chiffres portent sur les <strong>{c.enService} instrument(s) en service</strong> des
            familles qui exigent un raccordement (pipettes, enceintes, sondes, balances…) —
            {' '}{c.concernes} au total, réformés compris. <strong>Ils ne s’additionnent pas en un
            taux de conformité</strong> : « raccordé sans pièce » n’est pas une nuance de
            « raccordé », c’est l’écart qu’un évaluateur relève.
          </p>

          {c.chainesRompues > 0 && (
            <div style={{ background: TONES.danger.fond, borderInlineStart: `3px solid ${TONES.danger.trait}`, borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--red-600)' }}>
                {c.chainesRompues} chaîne{c.chainesRompues > 1 ? 's' : ''} de raccordement interrompue{c.chainesRompues > 1 ? 's' : ''}.
              </strong>{' '}
              Un maillon au moins y est absent ou périmé. Ouvrez la chaîne : le maillon en cause
              y est nommé, avec ce qu’il faut aller chercher.
            </div>
          )}
          {c.etalonsPerimes > 0 && (
            <div style={{ background: TONES.danger.fond, borderInlineStart: `3px solid ${TONES.danger.trait}`, borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--red-600)' }}>{c.etalonsPerimes} étalon(s) au certificat périmé.</strong>{' '}
              Un étalon expiré emmène avec lui tous les instruments qu’il raccorde — c’est
              l’échéance qu’on voit le moins venir.
            </div>
          )}

          <div style={{ display: 'flex', gap: 6 }} role="tablist">
            <Segment ariaLabel="Section" valeur={onglet} onChange={(valeur) => { setOnglet(valeur); setQ(''); setFiltre('tous'); }} options={([
              ['chaine', `Chaîne du parc (${equipements.length})`],
              ['etalons', `Étalons et matériaux de référence (${etalons.length})`],
              /* ⚠️ LE REGISTRE DES VÉRIFICATIONS (26/08/2026). Jusqu'ici, une
                 vérification ne laissait qu'une DATE sur la fiche de
                 l'équipement, écrasée à chaque passage : ni ce qui a été mesuré,
                 ni avec quel étalon, ni quel écart. « Vérifié le 12 mars » n'est
                 pas une preuve de vérification. */
              ['verifs', 'Vérifications'],
            ] as [typeof onglet, string][]).map(([k, lab]) => ({ valeur: k, libelle: lab }))} />
          </div>

          {onglet === 'chaine' && (
            <>
              <FilterBar
                q={q} onQ={setQ} placeholder="Rechercher un instrument, un lieu, un organisme…"
                chips={[
                  { k: 'tous', label: 'Tous', n: equipements.length },
                  { k: 'documente', label: 'Raccordé et documenté', n: c.raccordeDocumente },
                  { k: 'sanspiece', label: 'Raccordé, sans pièce', n: c.raccordeSansPiece },
                  { k: 'nonraccorde', label: 'Non raccordé', n: c.nonRaccorde },
                  { k: 'rompues', label: 'Chaîne interrompue', n: c.chainesRompues },
                ]}
                actif={filtre} onChip={setFiltre}
                total={equipements.length} affiches={chaines.length}
                onReset={() => { setQ(''); setFiltre('tous'); }} />
              <div style={{ ...card, padding: 16 }}>
                {chaines.length > 0 && (
                  <BoutonsTri label="Trier la chaîne du parc" tri={tri} onTri={onTri} style={{ paddingBottom: 6 }}
                    options={[{ cle: 'code', libelle: 'Code' }, { cle: 'nom', libelle: 'Nom' }, { cle: 'lieu', libelle: 'Lieu' }, { cle: 'etat', libelle: 'État' }]} />
                )}
                {chaines.map((e) => <LigneChaine key={e.documentId} e={e} />)}
                {chaines.length === 0 && (
                  <EmptyState
                    titre={equipements.length === 0 ? 'Aucun instrument du parc n’exige un raccordement.' : 'Aucun instrument ne correspond à ces critères.'}
                    indice={equipements.length === 0
                      ? 'La question ne se pose que pour les familles marquées « métrologie » : pipettes, enceintes, sondes, balances, chronomètres…'
                      : 'Élargissez la recherche ou revenez à « Tous ».'} />
                )}
              </div>
            </>
          )}

          {onglet === 'etalons' && (
            <>
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Ce registre est <strong>distinct des matériaux reconstitués</strong> : un calibrant
                règle un automate, un contrôle CQI vérifie la mesure, un étalon <em>porte la valeur
                de référence</em> — et c’est le seul des trois qui puisse raccorder.
              </p>
              {peutParc && !edition.ouvert && (
                <div>
                  <Bouton variante="primaire" taille="sm" type="button" onClick={() => setEdition({ ouvert: true, etalon: null })}>Enregistrer un étalon</Bouton>
                </div>
              )}
              {edition.ouvert && (
                <FormEtalon initial={edition.etalon}
                  equipementsMetro={equipements.map((e) => ({ code: e.code, nom: e.nom }))}
                  onFerme={() => setEdition({ ouvert: false, etalon: null })}
                  onEnregistre={() => { setEdition({ ouvert: false, etalon: null }); void charger(); }} />
              )}
              <FilterBar
                q={q} onQ={setQ} placeholder="Rechercher un étalon, une grandeur, un organisme…"
                chips={[]} actif="tous" onChip={() => {}}
                total={etalons.length} affiches={listeEtalons.length}
                onReset={() => setQ('')} />
              <div style={{ ...card, padding: 16 }}>
                {listeEtalons.map((e) => (
                  <LigneEtalon key={e.documentId} e={e} peutParc={peutParc}
                    onEditer={() => setEdition({ ouvert: true, etalon: e })}
                    onChange={() => void charger()} />
                ))}
                {listeEtalons.length === 0 && (
                  <EmptyState
                    titre={etalons.length === 0 ? 'Aucun étalon n’est encore enregistré.' : 'Aucun étalon ne correspond à cette recherche.'}
                    indice={etalons.length === 0
                      ? 'Tant que ce registre est vide, aucun étalonnage interne ne peut se raccorder : la comparaison n’aurait aucune référence.'
                      : 'Élargissez la recherche.'} />
                )}
              </div>
            </>
          )}

          {onglet === 'verifs' && (
            <PanneauVerifications equipements={equipements} etalons={etalons} estRQ={estRQ} peutParc={peutParc} />
          )}
        </>
      )}
    </div>
  );
}
